import type {
  ResourceRecordPayload,
  SdkCreateRecordHandle,
  SdkResource,
  SdkResourceType,
  SdkResourceRecord,
  SdkUseCreateRecordOptions
} from '@Pages/pageRuntime/sdk/types';
import { useRef } from 'react';

import {
  bookingIdempotencyFieldKey,
  type ManagedIdempotencyKey,
  withIdempotencyKey
} from './idempotencyKeys';
import { useRecordMutation } from './useRecordMutation';

type CreateRecordResource = Pick<SdkResource, 'create' | 'capabilities'>;

/**
 * Reactive wrapper around `resource.create` for pages that write records without a
 * form hook — a card grid's "Add" button, a bulk import, an inline composer. The
 * request runs through the resource handle, so the page's cached record data is
 * refreshed on success exactly as it is for a direct `resource.create` call.
 *
 * Exposed via `Sdk.hooks.useCreateRecord(resource, options)`.
 */
export function useCreateRecord<TResource extends SdkResourceType & CreateRecordResource>(
  resource: TResource,
  options?: SdkUseCreateRecordOptions<SdkResourceRecord<TResource>>
): SdkCreateRecordHandle<SdkResourceRecord<TResource>>;
export function useCreateRecord(
  resource: CreateRecordResource,
  options?: SdkUseCreateRecordOptions
): SdkCreateRecordHandle {
  const idempotent = options?.idempotent === true;
  const managedKey = useRef<ManagedIdempotencyKey | null>(null);
  const { run, reset, ...state } = useRecordMutation((payload: ResourceRecordPayload) => {
    if (!idempotent) {
      return resource.create(payload).then((response) => response.data);
    }

    const stamped = withIdempotencyKey(
      payload,
      bookingIdempotencyFieldKey(resource),
      managedKey.current
    );
    managedKey.current = stamped.managed;

    // A replayed booking is the record that the first attempt made, so it resolves as a success.
    return resource.create(stamped.payload).then((response) => {
      managedKey.current = null;

      return response.data;
    });
  }, options);

  return {
    ...state,
    create: (payload) => {
      if (idempotent) {
        bookingIdempotencyFieldKey(resource);
      }

      return run(payload);
    },
    reset: () => {
      managedKey.current = null;
      reset();
    }
  };
}
